import IconButton from "@pathwright/ui/src/components/button/IconButton"
import DropdownTrigger from "@pathwright/ui/src/components/dropdown/DropdownTrigger"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import Tooltip from "@pathwright/ui/src/components/tooltip/Tooltip"
import { useMemo, useState } from "react"
import CustomColors from "./CustomColors"
import {
  blockStyleMenuOptions,
  defaultBlockStyles,
  defaultStyleOptions
} from "./base-style-defaults"
import {
  DropdownButton,
  StyleItem,
  StyleMenu,
  StyleMenuWrapper,
  StyleSwitcherContainer,
  StyleToolWrapper,
  ToolHeader,
  offBlack
} from "./ui"
import {
  getCustomColors,
  getStylesForColorChange,
  getStylesForOption,
  setCustomColors
} from "./utils"

const BlockStylesMenu = (props) => {
  const {
    currentStyles,
    onChangeStyles,
    onOpenMenu,
    onCloseMenu,
    preventTooltip
  } = props

  const [customColorsOpen, setCustomColorsOpen] = useState(false)

  const blockStyles = useMemo(
    () => currentStyles || defaultBlockStyles,
    [currentStyles]
  )

  // Reference structure in `./base-style-defaults.js`
  const handleChangeStyles = (toolKey, option) => () => {
    const nextStyles = getStylesForOption(toolKey, option, blockStyles)

    onChangeStyles(nextStyles)
  }

  const handleChangeCustomColor = (nextCustomColors) => {
    // Persist custom colors in local storage for MVP version
    setCustomColors(nextCustomColors)

    const nextStyles = getStylesForColorChange(nextCustomColors, blockStyles)

    setCustomColorsOpen(false)
    onChangeStyles(nextStyles)
  }

  const handleClickCustomColor = (hasCustomColors, customColors) => () => {
    // If no custom colors exist, we should open the color picker
    if (!hasCustomColors) return setCustomColorsOpen(true)

    // Otherwise, we should choose the existing color scheme
    handleChangeCustomColor(customColors)
  }

  const getColors = () => {
    // Use stored custom colors if they exist

    const colorsInStorage = getCustomColors()

    if (
      colorsInStorage &&
      (colorsInStorage.color || colorsInStorage.backgroundColor)
    )
      return colorsInStorage

    // If no stored colors, return defaults
    const stylesSource = defaultStyleOptions
    return stylesSource.block.color.custom
  }

  const getHasCustomColors = (customColors) => {
    return customColors
      ? !!customColors.color || !!customColors.backgroundColor
      : false
  }

  const handleClearCustomColors = () => {
    setCustomColorsOpen(false)
    handleChangeStyles("blockColor", "lightest")()
  }

  const { selectedSettings } = blockStyles

  // Currently we have only the block styles menu;
  // will need to refactor if/when we add other menu options (ex: animation)
  const { tools } = blockStyleMenuOptions.block

  const customColors = getColors()
  const hasCustomColors = getHasCustomColors(customColors)

  return (
    <DropdownTrigger
      alignment="left"
      closeOnOverlayClick={false}
      onHide={onCloseMenu}
      onClick={() => {
        // When opening or closing the blocks styles menu:
        //  1. Close the color picker menu in case it's open
        //  2. Tell parent (BlockEditorContainer) to reset the in-state styles
        setCustomColorsOpen(false)
        onOpenMenu()
      }}
      renderDropdown={(props) => {
        return (
          <StyleSwitcherContainer>
            {customColorsOpen ? (
              <CustomColors
                color={customColors.color}
                backgroundColor={customColors.backgroundColor}
                onGoBack={() => setCustomColorsOpen(false)}
                onClearCustomColors={handleClearCustomColors}
                onSubmitColors={handleChangeCustomColor}
                hasCustomColors={hasCustomColors}
              />
            ) : (
              <StyleMenuWrapper>
                {tools.map((tool, i) => (
                  <StyleToolWrapper key={tool.key}>
                    <ToolHeader>
                      <span>{tool.label}</span>
                    </ToolHeader>
                    <StyleMenu
                      style={
                        tool.key === "blockWidth" || tool.key === "blockHeight"
                          ? {
                              border: "1px solid rgba(0,0,0,0.2)",
                              padding: "0",
                              borderRadius: "7px",
                              overflow: "hidden"
                            }
                          : hasCustomColors
                          ? { marginBottom: "5px" }
                          : null
                      }
                    >
                      {tool.options.map((toolOption, i) => {
                        const selected =
                          selectedSettings[tool.key] === toolOption

                        const style =
                          tool.key === "blockColor"
                            ? {
                                ...tool.handler(toolOption),
                                boxShadow: selected
                                  ? "inset 0 0 0 2px white, 0 0 0 2px rgba(100,100,100)"
                                  : "",
                                borderRadius: "7px",
                                margin: "0 8px 0 0",
                                padding: "5px 10px",
                                border:
                                  toolOption === "lightest"
                                    ? "solid 1px #efefef"
                                    : ""
                              }
                            : {
                                borderRight:
                                  i < tools.length - 1
                                    ? "1px solid lightgray"
                                    : ""
                              }

                        if (toolOption === "custom") {
                          return (
                            <div
                              style={{
                                borderLeft: "2px solid lightgray"
                              }}
                              key={i}
                            >
                              <StyleItem
                                className="BlocksStyles__StyleItem"
                                selected={selected}
                                applyHoverShadows={true}
                                style={{
                                  ...style,
                                  marginLeft: "10px",
                                  width: "auto",
                                  color: customColors.color,
                                  border: !hasCustomColors
                                    ? "1px solid lightgray"
                                    : "",
                                  backgroundColor: customColors.backgroundColor
                                }}
                                onClick={handleClickCustomColor(
                                  hasCustomColors,
                                  customColors
                                )}
                              >
                                {!hasCustomColors ? (
                                  <Pathicon icon="plus-circle" />
                                ) : (
                                  "T"
                                )}
                              </StyleItem>
                            </div>
                          )
                        }

                        return (
                          <StyleItem
                            key={i}
                            selected={selected}
                            style={style}
                            applyHoverShadows={tool.key === "blockColor"}
                            onClick={handleChangeStyles(tool.key, toolOption)}
                          >
                            {tool.icons && (
                              <Pathicon icon={tool.icons[toolOption]} />
                            )}
                            {tool.type === "color" && "T"}
                          </StyleItem>
                        )
                      })}
                    </StyleMenu>
                    {tool.key === "blockColor" && hasCustomColors && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end"
                        }}
                      >
                        <IconButton
                          onClick={() => setCustomColorsOpen(true)}
                          style={{ cursor: "pointer", width: "auto" }}
                          color={offBlack}
                          icon="edit"
                        >
                          edit
                        </IconButton>
                      </div>
                    )}
                  </StyleToolWrapper>
                ))}
              </StyleMenuWrapper>
            )}
          </StyleSwitcherContainer>
        )
      }}
    >
      <DropdownButton>
        <Tooltip title={!preventTooltip && "Customize the look of this block"}>
          <Pathicon icon="brush-2" />
        </Tooltip>
      </DropdownButton>
    </DropdownTrigger>
  )
}

export default BlockStylesMenu
